import type { JSX } from 'react';
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';

import { useGetUsersListingQuery } from 'features/users/services/users.api';
import type { UserListItem } from 'features/users/types/user';
import { Check, Loader2, Save, Search } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

import useActivateNotification from '../hooks/useActivateNotification';
import useNotificationConfig from '../hooks/useNotificationConfig';
import { useGetAdminRolesQuery } from '../services/adminRoles.api';
import type { NotificationConfigValue, NotificationEventType } from '../types/notificationConfig';
import {
  DEFAULT_NOTIFICATION_CONFIG,
  NOTIFICATION_EVENT_TYPES,
  NOTIFICATION_EVENT_TYPE_LABELS,
} from '../types/notificationConfig';
import SubProjectAdvancedConfig from './SubProjectAdvancedConfig';

type DomainKey = 'alert' | 'task' | 'question';

const DOMAIN_KEYS: DomainKey[] = ['alert', 'task', 'question'];

const DOMAIN_LABELS: Record<DomainKey, string> = {
  alert: 'Alertas',
  task: 'Tareas',
  question: 'Preguntas',
};

interface SubProjectOption {
  id: number;
  name: string;
}

interface AlertsDirectedConfigProps {
  projectId: number;
  businessRuleDisplayName?: string;
  subProjects?: SubProjectOption[];
}

type FeedbackState = {
  title: string;
  message: string;
  variant: 'success' | 'error';
};

type AdditionalUser = {
  id: number;
  name: string | null;
  email: string;
};

const clampScore = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
};

const getDomainRoles = (config: NotificationConfigValue, domain: DomainKey): string[] =>
  config.domains[domain]?.roles ?? [];

const setDomainRoles = (
  config: NotificationConfigValue,
  domain: DomainKey,
  roles: string[]
): NotificationConfigValue => ({
  ...config,
  domains: {
    ...config.domains,
    [domain]: { roles: [...new Set(roles)] },
  },
});

const normalizeSearch = (value: string): string => value.trim().toLowerCase();

const toDomId = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-') || 'item';

const userMatchesSearch = (user: { name: string | null; email: string | null }, normalizedSearch: string): boolean => {
  if (!normalizedSearch) {
    return true;
  }

  return (
    (user.name ?? '').toLowerCase().includes(normalizedSearch) ||
    (user.email ?? '').toLowerCase().includes(normalizedSearch)
  );
};

const getUserDisplayName = (user: AdditionalUser): string => user.name || user.email;

const getUserInitials = (user: AdditionalUser): string => {
  const baseValue = getUserDisplayName(user).trim();
  const initials = baseValue
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (initials || user.email[0] || '?').toLowerCase();
};

interface AdditionalUserRowProps {
  user: AdditionalUser;
  isSelected: boolean;
  onToggle: (userId: number, checked: boolean) => void;
}

const AdditionalUserRow = ({ user, isSelected, onToggle }: AdditionalUserRowProps): JSX.Element => {
  const inputId = `additional-user-${user.id}`;

  return (
    <li>
      <label
        htmlFor={inputId}
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-body transition-colors hover:bg-bg-surface-raised',
          isSelected && 'font-medium'
        )}
      >
        <input
          id={inputId}
          type="checkbox"
          className="size-4 shrink-0 rounded border-input accent-brand-vivid"
          checked={isSelected}
          onChange={(event) => onToggle(user.id, event.target.checked)}
        />
        <span
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            isSelected ? 'bg-brand-vivid/10 text-brand-vivid' : 'bg-bg-surface-raised text-text-muted'
          )}
          aria-hidden
        >
          {getUserInitials(user)}
        </span>
        <span className="min-w-0 flex-1 text-text-default">
          <span className={cn('block truncate', isSelected && 'font-medium')}>{getUserDisplayName(user)}</span>
          <span className="block truncate text-xs text-text-muted">{user.email}</span>
        </span>
        {isSelected ? <Check className="size-4 shrink-0 text-success" aria-hidden /> : null}
      </label>
    </li>
  );
};

const AlertsDirectedConfig = ({
  projectId,
  businessRuleDisplayName,
  subProjects = [],
}: AlertsDirectedConfigProps): JSX.Element => {
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isUsersSheetOpen, setIsUsersSheetOpen] = useState(false);
  const [draft, setDraft] = useState<NotificationConfigValue | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [subProjectUsersSheet, setSubProjectUsersSheet] = useState<{ subProjectId: number } | null>(null);
  const [subProjectUserSearch, setSubProjectUserSearch] = useState('');
  const [eventTypeUsersSheet, setEventTypeUsersSheet] = useState<{ eventType: NotificationEventType } | null>(null);
  const [eventTypeUserSearch, setEventTypeUserSearch] = useState('');

  const { config, isLoading, isSaving, updateConfig, refetch, businessRuleId, hasPubsubEvent, isActive } =
    useNotificationConfig(projectId, true);

  const { data: roles = [] } = useGetAdminRolesQuery({ includeInactive: false });
  // The listing endpoint takes exactly one of projectId / onlyAdmins, and admins have no projectId,
  // so both lists are needed: admins are selectable recipients but never belong to the project.
  const { data: projectUsersData } = useGetUsersListingQuery({ projectId });
  const { data: adminUsersData } = useGetUsersListingQuery({ onlyAdmins: true });

  const sortedUsers = useMemo(() => {
    const byId = new Map<number, UserListItem>();
    [...(projectUsersData?.users ?? []), ...(adminUsersData?.users ?? [])].forEach((user) => {
      byId.set(user.id, user);
    });

    return [...byId.values()].sort(
      (a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', 'es', { sensitivity: 'base' }) || a.email.localeCompare(b.email)
    );
  }, [adminUsersData, projectUsersData]);
  const deferredUserSearch = useDeferredValue(userSearch);

  // Reset draft when project changes
  useEffect(() => {
    setDraft(null);
  }, [projectId]);

  useEffect(() => {
    if (!isUsersSheetOpen) {
      setUserSearch('');
      setShowOnlySelected(false);
    }
  }, [isUsersSheetOpen]);

  // Seed draft from loaded config (only once per load)
  useEffect(() => {
    if (config && draft === null) {
      setDraft(config);
    }
  }, [config, draft]);

  const handleActivated = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  const { activate, isActivating, activationError } = useActivateNotification({
    projectId,
    existingBusinessRuleId: businessRuleId,
    hasPubsubEvent,
    businessRuleDisplayName,
    onActivated: handleActivated,
  });

  const hasActiveConfig = isActive && Boolean(config || draft);

  const handleSave = async (): Promise<void> => {
    if (!draft) return;
    try {
      await updateConfig(draft);
      setFeedback({
        title: 'Configuración guardada',
        message: 'La configuración de alertas dirigidas se actualizó correctamente.',
        variant: 'success',
      });
      await refetch();
    } catch (error: unknown) {
      setFeedback({
        title: 'Error al guardar',
        message: extractErrorMessage(error, 'No fue posible guardar la configuración.'),
        variant: 'error',
      });
    }
  };

  const handleConfirmActivation = async (): Promise<void> => {
    try {
      await activate();
      setIsConfirmOpen(false);
      setFeedback({
        title: 'Notificaciones activadas',
        message: 'Las notificaciones push se activaron correctamente para este proyecto.',
        variant: 'success',
      });
    } catch {
      // activationError surfaces below
    }
  };

  // ── Keyboard close for confirm dialog ────────────────────────────────────
  useEffect(() => {
    if (!isConfirmOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsConfirmOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isConfirmOpen]);

  // ── Derived values ────────────────────────────────────────────────────────
  const workingConfig = draft ?? DEFAULT_NOTIFICATION_CONFIG;
  const allRolesInUse = useMemo(
    () => [
      ...getDomainRoles(workingConfig, 'alert'),
      ...getDomainRoles(workingConfig, 'task'),
      ...getDomainRoles(workingConfig, 'question'),
    ],
    [workingConfig]
  );

  // Roles already in the config but missing from the API (inactive/non-assignable) fall back to their name
  const roleLabels = useMemo(() => new Map(roles.map((r) => [r.name, r.displayName])), [roles]);
  const roleOptions = useMemo(
    () =>
      [...new Set([...roles.map((r) => r.name), ...allRolesInUse])].sort((a, b) =>
        (roleLabels.get(a) ?? a).localeCompare(roleLabels.get(b) ?? b)
      ),
    [roles, allRolesInUse, roleLabels]
  );
  const selectedUserIds = workingConfig.includeUsers.all;
  const selectedUserIdSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);
  const selectedUsers = useMemo(
    () => sortedUsers.filter((user) => selectedUserIdSet.has(user.id)),
    [selectedUserIdSet, sortedUsers]
  );
  const normalizedUserSearch = normalizeSearch(deferredUserSearch);
  const filteredUsers = useMemo(
    () => sortedUsers.filter((user) => userMatchesSearch(user, normalizedUserSearch)),
    [normalizedUserSearch, sortedUsers]
  );
  const visibleSelectedUsers = useMemo(
    () => filteredUsers.filter((user) => selectedUserIdSet.has(user.id)),
    [filteredUsers, selectedUserIdSet]
  );
  const visibleUnselectedUsers = useMemo(
    () => (showOnlySelected ? [] : filteredUsers.filter((user) => !selectedUserIdSet.has(user.id))),
    [filteredUsers, selectedUserIdSet, showOnlySelected]
  );
  const visibleUserCount = visibleSelectedUsers.length + visibleUnselectedUsers.length;
  const selectedPreviewUsers = selectedUsers.slice(0, 3);
  const hiddenSelectedCount = Math.max(selectedUsers.length - selectedPreviewUsers.length, 0);
  const hasUserSearch = normalizedUserSearch.length > 0;
  const shouldShowOtherUsersSeparator = visibleSelectedUsers.length > 0 && visibleUnselectedUsers.length > 0;
  const drawerEmptyMessage =
    showOnlySelected && !hasUserSearch
      ? 'Aún no agregaste usuarios adicionales.'
      : 'No hay usuarios que coincidan con la búsqueda.';
  const { considerAssignment, minScore } = workingConfig.configs.default;
  const subProjectById = useMemo(() => new Map(subProjects.map((s) => [s.id, s])), [subProjects]);
  const hasPendingChanges = draft !== null && config !== null && JSON.stringify(draft) !== JSON.stringify(config);

  // ── Setters ───────────────────────────────────────────────────────────────
  const toggleDomainRole = (domain: DomainKey, roleName: string, checked: boolean): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      const current = getDomainRoles(base, domain);
      const next = checked ? [...current, roleName] : current.filter((r) => r !== roleName);
      return setDomainRoles(base, domain, next);
    });
  };

  const toggleUser = (userId: number, checked: boolean): void => {
    const current = workingConfig.includeUsers.all;
    const next = checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId);
    setDraft((prev) => ({
      ...(prev ?? DEFAULT_NOTIFICATION_CONFIG),
      includeUsers: { ...(prev ?? DEFAULT_NOTIFICATION_CONFIG).includeUsers, all: next },
    }));
  };

  const clearSelectedUsers = (): void => {
    setDraft((prev) => ({
      ...(prev ?? DEFAULT_NOTIFICATION_CONFIG),
      includeUsers: { ...(prev ?? DEFAULT_NOTIFICATION_CONFIG).includeUsers, all: [] },
    }));
  };

  const setConsiderAssignment = (value: boolean): void => {
    setDraft((prev) => ({
      ...(prev ?? DEFAULT_NOTIFICATION_CONFIG),
      configs: {
        ...(prev ?? DEFAULT_NOTIFICATION_CONFIG).configs,
        default: { ...(prev ?? DEFAULT_NOTIFICATION_CONFIG).configs.default, considerAssignment: value },
      },
    }));
  };

  const setMinScore = (value: number): void => {
    setDraft((prev) => ({
      ...(prev ?? DEFAULT_NOTIFICATION_CONFIG),
      configs: {
        ...(prev ?? DEFAULT_NOTIFICATION_CONFIG).configs,
        default: { ...(prev ?? DEFAULT_NOTIFICATION_CONFIG).configs.default, minScore: clampScore(value) },
      },
    }));
  };

  const updateSubProjectScoreConfig = useCallback(
    (subProjectId: number, next: { minScore?: number; considerAssignment?: boolean }): void => {
      setDraft((prev) => {
        const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
        const existing = base.configs.byProjects[String(subProjectId)] ?? { minScore: 0, considerAssignment: false };
        return {
          ...base,
          configs: {
            ...base.configs,
            byProjects: {
              ...base.configs.byProjects,
              [subProjectId]: {
                minScore: clampScore(next.minScore ?? existing.minScore),
                considerAssignment: next.considerAssignment ?? existing.considerAssignment,
              },
            },
          },
        };
      });
    },
    []
  );

  const removeSubProjectScoreConfig = useCallback((subProjectId: number): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      const nextByProjects = { ...base.configs.byProjects };
      delete nextByProjects[String(subProjectId)];
      return {
        ...base,
        configs: { ...base.configs, byProjects: nextByProjects },
      };
    });
  }, []);

  const openSubProjectUsersSheet = useCallback((subProjectId: number): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      const key = String(subProjectId);
      if (key in base.includeUsers.byProjectUser) {
        return base;
      }

      return {
        ...base,
        includeUsers: {
          ...base.includeUsers,
          byProjectUser: {
            ...base.includeUsers.byProjectUser,
            [key]: [],
          },
        },
      };
    });
    setSubProjectUsersSheet({ subProjectId });
  }, []);

  const toggleSubProjectUser = (subProjectId: number, userId: number, checked: boolean): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      const key = String(subProjectId);
      const current = base.includeUsers.byProjectUser[key] ?? [];
      const next = checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId);
      return {
        ...base,
        includeUsers: {
          ...base.includeUsers,
          byProjectUser: { ...base.includeUsers.byProjectUser, [key]: next },
        },
      };
    });
  };

  const removeSubProjectUsers = useCallback((subProjectId: number): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      const key = String(subProjectId);
      const nextByProjectUser = { ...base.includeUsers.byProjectUser };
      delete nextByProjectUser[key];
      return {
        ...base,
        includeUsers: { ...base.includeUsers, byProjectUser: nextByProjectUser },
      };
    });
  }, []);

  // Event domain is a pure on/off switch: roles never affect event delivery
  // (recipients are the client's assigned user + includeUsers.byEventType).
  const setEventDomainEnabled = (enabled: boolean): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      return {
        ...base,
        domains: { ...base.domains, event: enabled ? { roles: [] } : null },
      };
    });
  };

  const openEventTypeUsersSheet = useCallback((eventType: NotificationEventType): void => {
    setEventTypeUsersSheet({ eventType });
  }, []);

  const toggleEventTypeUser = (eventType: NotificationEventType, userId: number, checked: boolean): void => {
    setDraft((prev) => {
      const base = prev ?? DEFAULT_NOTIFICATION_CONFIG;
      const current = base.includeUsers.byEventType[eventType] ?? [];
      const next = checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId);
      const nextByEventType = { ...base.includeUsers.byEventType };
      if (next.length === 0) {
        delete nextByEventType[eventType];
      } else {
        nextByEventType[eventType] = next;
      }
      return {
        ...base,
        includeUsers: { ...base.includeUsers, byEventType: nextByEventType },
      };
    });
  };

  let drawerUsersContent: JSX.Element;
  if (sortedUsers.length === 0) {
    drawerUsersContent = (
      <p className="m-0 px-3 py-4 text-sm text-text-subtle font-body">No hay usuarios disponibles.</p>
    );
  } else if (visibleUserCount === 0) {
    drawerUsersContent = <p className="m-0 px-3 py-4 text-sm text-text-subtle font-body">{drawerEmptyMessage}</p>;
  } else {
    drawerUsersContent = (
      <div>
        {visibleSelectedUsers.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {visibleSelectedUsers.map((user) => (
              <AdditionalUserRow key={user.id} user={user} isSelected onToggle={toggleUser} />
            ))}
          </ul>
        ) : null}

        {shouldShowOtherUsersSeparator ? (
          <div className="my-2 px-3">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
              <span>Otros usuarios</span>
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
            </div>
          </div>
        ) : null}

        {visibleUnselectedUsers.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {visibleUnselectedUsers.map((user) => (
              <AdditionalUserRow
                key={user.id}
                user={user}
                isSelected={selectedUserIdSet.has(user.id)}
                onToggle={toggleUser}
              />
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {feedback ? (
          <div
            className={cn(
              'flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm font-body',
              feedback.variant === 'success'
                ? 'border-success/40 bg-success-light text-success'
                : 'border-error/40 bg-error-light text-error'
            )}
            role="status"
          >
            <div>
              <p className="m-0 font-semibold">{feedback.title}</p>
              <p className="m-0 mt-1">{feedback.message}</p>
            </div>
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => setFeedback(null)} aria-label="Cerrar">
              ×
            </Button>
          </div>
        ) : null}

        {!hasActiveConfig ? (
          <div
            className="flex flex-col gap-3 rounded-lg border border-warning bg-warning-light p-4 sm:flex-row sm:items-center
              sm:justify-between"
            role="status"
          >
            <p className="m-0 text-sm text-neutral-900 font-body">
              {isLoading || isActivating
                ? 'Verificando estado de notificaciones...'
                : 'Este proyecto no tiene notificaciones push activadas.'}
            </p>
            <Button
              type="button"
              className="shrink-0 bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
              disabled={isLoading || isActivating}
              onClick={() => setIsConfirmOpen(true)}
            >
              Activar
            </Button>
          </div>
        ) : null}

        {activationError ? (
          <div className="rounded-lg border border-warning bg-warning-light px-3 py-2 text-sm text-neutral-900 font-body">
            {activationError}
          </div>
        ) : null}

        {hasActiveConfig ? (
          <div className="flex flex-col gap-5 pb-6">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-text-muted font-body">
                <Loader2 className="size-5 animate-spin shrink-0" aria-hidden />
                Cargando configuración...
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="m-0 text-sm font-semibold text-brand-navy font-display">
                      ¿Qué roles reciben las notificaciones?
                    </p>
                    <p className="m-0 mt-1 text-xs text-text-muted font-body">
                      Configura los roles por dominio. El rol <strong>manager</strong> recibe siempre.
                    </p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    {DOMAIN_KEYS.map((domain) => {
                      const domainRoles = getDomainRoles(workingConfig, domain);
                      return (
                        <fieldset
                          key={domain}
                          className="m-0 rounded-lg border border-border-subtle bg-bg-surface p-4 shadow-xs"
                        >
                          <legend className="px-1 text-sm font-semibold text-brand-navy font-display">
                            {DOMAIN_LABELS[domain]}
                          </legend>
                          <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
                            {roleOptions.map((roleName) => {
                              const roleInputId = `notification-role-${domain}-${toDomId(roleName)}`;
                              return (
                                <li key={roleName}>
                                  <label
                                    htmlFor={roleInputId}
                                    className="flex cursor-pointer items-center gap-2 text-sm font-body"
                                  >
                                    <input
                                      id={roleInputId}
                                      type="checkbox"
                                      className="size-4 shrink-0 rounded border-input accent-brand-vivid"
                                      checked={domainRoles.includes(roleName)}
                                      onChange={(e) => toggleDomainRole(domain, roleName, e.target.checked)}
                                    />
                                    <span className="text-text-default">{roleLabels.get(roleName) ?? roleName}</span>
                                  </label>
                                </li>
                              );
                            })}
                            {roleOptions.length === 0 ? (
                              <li className="text-sm text-text-subtle font-body">Sin roles disponibles.</li>
                            ) : null}
                          </ul>
                        </fieldset>
                      );
                    })}
                  </div>
                </div>

                <fieldset className="m-0 rounded-lg border border-border-subtle bg-bg-surface p-4 shadow-xs">
                  <legend className="px-1 text-sm font-semibold text-brand-navy font-display">Eventos</legend>
                  <div className="flex items-start justify-between gap-3">
                    <p className="m-0 mt-1 text-xs text-text-muted font-body">
                      Los eventos se notifican al ejecutivo asignado del cliente y a los usuarios por tipo de evento —
                      los roles no aplican a este dominio.
                    </p>
                    <label
                      htmlFor="event-domain-enabled-checkbox"
                      className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-text-default font-body"
                    >
                      <input
                        id="event-domain-enabled-checkbox"
                        type="checkbox"
                        className="size-4 rounded border-input accent-brand-vivid"
                        checked={workingConfig.domains.event !== null}
                        onChange={(e) => setEventDomainEnabled(e.target.checked)}
                      />
                      Habilitado
                    </label>
                  </div>

                  {workingConfig.domains.event !== null ? (
                    <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
                      {NOTIFICATION_EVENT_TYPES.map((eventType) => {
                        const eventUserIds = workingConfig.includeUsers.byEventType[eventType] ?? [];
                        return (
                          <li
                            key={eventType}
                            className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-surface-raised px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="m-0 text-sm text-text-default font-body">
                                {NOTIFICATION_EVENT_TYPE_LABELS[eventType]}
                              </p>
                              <p className="m-0 text-xs text-text-muted font-body">
                                {eventUserIds.length === 0
                                  ? 'Solo el ejecutivo asignado'
                                  : `Asignado + ${eventUserIds.length} ${
                                      eventUserIds.length === 1 ? 'usuario' : 'usuarios'
                                    }`}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              onClick={() => openEventTypeUsersSheet(eventType)}
                            >
                              Editar usuarios ({eventUserIds.length})
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </fieldset>

                <fieldset className="m-0 rounded-lg border border-border-subtle bg-bg-surface p-4 shadow-xs">
                  <legend className="px-1 text-sm font-semibold text-brand-navy font-display">
                    Destinatarios adicionales
                  </legend>
                  <p className="mb-3 mt-1 text-xs text-text-muted font-body">
                    Reciben notificaciones aunque su rol no esté seleccionado.
                  </p>
                  <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border-subtle bg-bg-surface-raised p-3">
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-sm font-semibold text-brand-navy font-display">
                        Destinatarios adicionales
                      </p>
                      <p className="m-0 mt-1 text-xs text-text-muted font-body">
                        Reciben notificaciones aunque su rol no esté seleccionado. Solo aplica a alertas, preguntas y
                        tareas: los eventos se notifican al ejecutivo asignado del cliente y a los usuarios configurados
                        por tipo de evento.
                      </p>
                      {selectedPreviewUsers.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {selectedPreviewUsers.map((user) => (
                            <Badge key={user.id} variant="outline" className="max-w-full truncate">
                              {getUserDisplayName(user)}
                            </Badge>
                          ))}
                          {hiddenSelectedCount > 0 ? (
                            <Badge variant="secondary">+{hiddenSelectedCount} más</Badge>
                          ) : null}
                        </div>
                      ) : (
                        <p className="m-0 mt-3 text-sm text-text-subtle font-body">
                          Aún no agregaste usuarios adicionales.
                        </p>
                      )}
                    </div>

                    <Button
                      type="button"
                      className="shrink-0 bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
                      onClick={() => setIsUsersSheetOpen(true)}
                    >
                      Editar destinatarios ({selectedUserIds.length})
                    </Button>
                  </div>
                </fieldset>

                <div className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 shadow-xs">
                  <div className="min-w-0">
                    <p className="m-0 text-sm font-semibold text-brand-navy font-display">¿Considerar asignación?</p>
                    <p className="m-0 text-xs text-text-muted font-body">
                      Si está activo, también se notifica al ejecutivo asignado al cliente.
                    </p>
                  </div>
                  <label
                    htmlFor="consider-assignment-checkbox"
                    className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-text-default font-body"
                  >
                    <input
                      id="consider-assignment-checkbox"
                      type="checkbox"
                      className="size-4 rounded border-input accent-brand-vivid"
                      checked={considerAssignment}
                      onChange={(e) => setConsiderAssignment(e.target.checked)}
                    />
                    Sí
                  </label>
                </div>

                <div className="rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 shadow-xs">
                  <div className="mb-3 flex items-baseline justify-between gap-3">
                    <div>
                      <p className="m-0 text-sm font-semibold text-brand-navy font-display">
                        Score mínimo para notificar
                      </p>
                      <p className="m-0 mt-1 text-xs text-text-muted font-body">
                        Sólo se notifican alertas cuyo score sea igual o superior a este valor (0-100).
                      </p>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={minScore}
                      className="h-9 w-20 shrink-0"
                      aria-label="Score mínimo"
                      onChange={(e) => setMinScore(Number(e.target.value))}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={minScore}
                    aria-label="Score mínimo (slider)"
                    className="w-full accent-brand-vivid"
                    onChange={(e) => setMinScore(Number(e.target.value))}
                  />
                </div>

                {subProjects.length > 0 ? (
                  <SubProjectAdvancedConfig
                    subProjects={subProjects}
                    byProjects={workingConfig.configs.byProjects}
                    byProjectUser={workingConfig.includeUsers.byProjectUser}
                    sortedUsers={sortedUsers}
                    selectedUserIdSet={selectedUserIdSet}
                    globalMinScore={minScore}
                    globalConsiderAssignment={considerAssignment}
                    onUpdateScoreConfig={updateSubProjectScoreConfig}
                    onRemoveScoreConfig={removeSubProjectScoreConfig}
                    onEditRecipients={openSubProjectUsersSheet}
                    onRemoveRecipients={removeSubProjectUsers}
                  />
                ) : null}
              </>
            )}

            {isLoading ? null : (
              <div className="sticky bottom-4 z-10 mt-6 flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 shadow-save-bar">
                <div className="flex min-w-0 items-center gap-2 text-xs text-text-muted font-body">
                  <span
                    className={cn('size-2 shrink-0 rounded-full', hasPendingChanges ? 'bg-warning' : 'bg-success')}
                    aria-hidden
                  />
                  <span className="truncate">
                    {hasPendingChanges ? 'Tienes cambios sin guardar.' : 'No hay cambios pendientes.'}
                  </span>
                </div>
                <Button
                  type="button"
                  disabled={isSaving || !hasPendingChanges}
                  className="shrink-0 gap-2 bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
                  onClick={() => {
                    handleSave().catch(() => {});
                  }}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="size-4" aria-hidden />
                      Guardar cambios
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <Sheet open={isUsersSheetOpen} onOpenChange={setIsUsersSheetOpen}>
        <SheetContent
          side="right"
          className="flex h-full w-[min(100vw,28rem)] max-w-none flex-col gap-0 overflow-hidden p-0"
        >
          <SheetHeader className="border-b border-border px-4 py-4 pr-14">
            <SheetTitle>Destinatarios adicionales</SheetTitle>
            <SheetDescription>
              <span className="font-semibold text-brand-navy">
                {selectedUserIds.length} de {sortedUsers.length}
              </span>{' '}
              seleccionados · reciben notificaciones aunque su rol no esté incluido.
            </SheetDescription>
          </SheetHeader>

          <div className="border-b border-border bg-bg-surface px-4 py-3">
            <div className="space-y-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
                <Input
                  type="search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Buscar por nombre o email"
                  className="h-9 pl-9 text-sm"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="show-only-selected-users"
                  className="flex cursor-pointer items-center gap-2 text-xs text-text-muted font-body"
                >
                  <input
                    id="show-only-selected-users"
                    type="checkbox"
                    className="size-3.5 shrink-0 rounded border-input accent-brand-vivid"
                    checked={showOnlySelected}
                    onChange={(event) => setShowOnlySelected(event.target.checked)}
                  />
                  Solo seleccionados
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-brand-vivid hover:text-brand-vivid"
                  disabled={selectedUserIds.length === 0}
                  onClick={clearSelectedUsers}
                >
                  Quitar todos
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-text-muted font-body">
                <span>
                  {hasUserSearch || showOnlySelected
                    ? `Mostrando ${visibleUserCount} de ${sortedUsers.length}`
                    : `${sortedUsers.length} usuarios disponibles`}
                </span>
                {hasUserSearch ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setUserSearch('')}
                  >
                    Limpiar
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <section className="min-h-0 flex-1 overflow-y-auto px-2 py-2">{drawerUsersContent}</section>

          <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-border bg-bg-surface/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-bg-surface/80">
            <span className="text-xs text-text-muted font-body">
              <strong className="text-brand-navy">{selectedUserIds.length}</strong>{' '}
              {selectedUserIds.length === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button
              type="button"
              className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
              onClick={() => setIsUsersSheetOpen(false)}
            >
              Listo
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={subProjectUsersSheet !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSubProjectUsersSheet(null);
            setSubProjectUserSearch('');
          }
        }}
      >
        <SheetContent
          side="right"
          className="flex h-full w-[min(100vw,28rem)] max-w-none flex-col gap-0 overflow-hidden p-0"
        >
          {((): JSX.Element | null => {
            if (subProjectUsersSheet === null) return null;
            const { subProjectId } = subProjectUsersSheet;
            const sub = subProjectById.get(subProjectId);
            const currentIds = workingConfig.includeUsers.byProjectUser[String(subProjectId)] ?? [];
            const currentIdSet = new Set(currentIds);
            const normalizedSubSearch = normalizeSearch(subProjectUserSearch);
            const filteredSubUsers = sortedUsers.filter((u) => userMatchesSearch(u, normalizedSubSearch));
            const visibleSubSelected = filteredSubUsers.filter((u) => currentIdSet.has(u.id));
            const visibleSubUnselected = filteredSubUsers.filter((u) => !currentIdSet.has(u.id));
            const showSubSeparator = visibleSubSelected.length > 0 && visibleSubUnselected.length > 0;
            return (
              <>
                <SheetHeader className="border-b border-border px-4 py-4 pr-14">
                  <SheetTitle>Destinatarios extra · {sub?.name ?? `Subproyecto #${subProjectId}`}</SheetTitle>
                  <SheetDescription>
                    <span className="font-semibold text-brand-navy">
                      {currentIds.length} de {sortedUsers.length}
                    </span>{' '}
                    seleccionados para este subproyecto.
                  </SheetDescription>
                </SheetHeader>
                <div className="border-b border-border bg-bg-surface px-4 py-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
                    <Input
                      type="search"
                      value={subProjectUserSearch}
                      onChange={(e) => setSubProjectUserSearch(e.target.value)}
                      placeholder="Buscar por nombre o email"
                      className="h-9 pl-9 text-sm"
                      autoFocus
                    />
                  </div>
                </div>
                <section className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                  {sortedUsers.length === 0 ? (
                    <p className="m-0 px-3 py-4 text-sm text-text-subtle font-body">No hay usuarios disponibles.</p>
                  ) : filteredSubUsers.length === 0 ? (
                    <p className="m-0 px-3 py-4 text-sm text-text-subtle font-body">
                      No hay usuarios que coincidan con la búsqueda.
                    </p>
                  ) : (
                    <div>
                      {visibleSubSelected.length > 0 ? (
                        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                          {visibleSubSelected.map((user) => (
                            <AdditionalUserRow
                              key={user.id}
                              user={user}
                              isSelected
                              onToggle={(uid, checked) => toggleSubProjectUser(subProjectId, uid, checked)}
                            />
                          ))}
                        </ul>
                      ) : null}
                      {showSubSeparator ? (
                        <div className="my-2 px-3">
                          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
                            <span>Otros usuarios</span>
                            <span className="h-px flex-1 bg-border-subtle" aria-hidden />
                          </div>
                        </div>
                      ) : null}
                      {visibleSubUnselected.length > 0 ? (
                        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                          {visibleSubUnselected.map((user) => (
                            <AdditionalUserRow
                              key={user.id}
                              user={user}
                              isSelected={false}
                              onToggle={(uid, checked) => toggleSubProjectUser(subProjectId, uid, checked)}
                            />
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )}
                </section>
                <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-border bg-bg-surface/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-bg-surface/80">
                  <Button
                    type="button"
                    className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
                    onClick={() => {
                      setSubProjectUsersSheet(null);
                      setSubProjectUserSearch('');
                    }}
                  >
                    Listo
                  </Button>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      <Sheet
        open={eventTypeUsersSheet !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEventTypeUsersSheet(null);
            setEventTypeUserSearch('');
          }
        }}
      >
        <SheetContent
          side="right"
          className="flex h-full w-[min(100vw,28rem)] max-w-none flex-col gap-0 overflow-hidden p-0"
        >
          {((): JSX.Element | null => {
            if (eventTypeUsersSheet === null) return null;
            const { eventType } = eventTypeUsersSheet;
            const currentIds = workingConfig.includeUsers.byEventType[eventType] ?? [];
            const currentIdSet = new Set(currentIds);
            const normalizedEventSearch = normalizeSearch(eventTypeUserSearch);
            const filteredEventUsers = sortedUsers.filter((u) => userMatchesSearch(u, normalizedEventSearch));
            const visibleEventSelected = filteredEventUsers.filter((u) => currentIdSet.has(u.id));
            const visibleEventUnselected = filteredEventUsers.filter((u) => !currentIdSet.has(u.id));
            const showEventSeparator = visibleEventSelected.length > 0 && visibleEventUnselected.length > 0;
            return (
              <>
                <SheetHeader className="border-b border-border px-4 py-4 pr-14">
                  <SheetTitle>Usuarios por evento · {NOTIFICATION_EVENT_TYPE_LABELS[eventType]}</SheetTitle>
                  <SheetDescription>
                    <span className="font-semibold text-brand-navy">
                      {currentIds.length} de {sortedUsers.length}
                    </span>{' '}
                    seleccionados · recibirán todas las notificaciones de este tipo de evento, además del ejecutivo
                    asignado.
                  </SheetDescription>
                </SheetHeader>
                <div className="border-b border-border bg-bg-surface px-4 py-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
                    <Input
                      type="search"
                      value={eventTypeUserSearch}
                      onChange={(e) => setEventTypeUserSearch(e.target.value)}
                      placeholder="Buscar por nombre o email"
                      className="h-9 pl-9 text-sm"
                      autoFocus
                    />
                  </div>
                </div>
                <section className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                  {sortedUsers.length === 0 ? (
                    <p className="m-0 px-3 py-4 text-sm text-text-subtle font-body">No hay usuarios disponibles.</p>
                  ) : filteredEventUsers.length === 0 ? (
                    <p className="m-0 px-3 py-4 text-sm text-text-subtle font-body">
                      No hay usuarios que coincidan con la búsqueda.
                    </p>
                  ) : (
                    <div>
                      {visibleEventSelected.length > 0 ? (
                        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                          {visibleEventSelected.map((user) => (
                            <AdditionalUserRow
                              key={user.id}
                              user={user}
                              isSelected
                              onToggle={(uid, checked) => toggleEventTypeUser(eventType, uid, checked)}
                            />
                          ))}
                        </ul>
                      ) : null}
                      {showEventSeparator ? (
                        <div className="my-2 px-3">
                          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
                            <span>Otros usuarios</span>
                            <span className="h-px flex-1 bg-border-subtle" aria-hidden />
                          </div>
                        </div>
                      ) : null}
                      {visibleEventUnselected.length > 0 ? (
                        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                          {visibleEventUnselected.map((user) => (
                            <AdditionalUserRow
                              key={user.id}
                              user={user}
                              isSelected={false}
                              onToggle={(uid, checked) => toggleEventTypeUser(eventType, uid, checked)}
                            />
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )}
                </section>
                <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-border bg-bg-surface/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-bg-surface/80">
                  <Button
                    type="button"
                    className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
                    onClick={() => {
                      setEventTypeUsersSheet(null);
                      setEventTypeUserSearch('');
                    }}
                  >
                    Listo
                  </Button>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {isConfirmOpen ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Cerrar diálogo"
            onClick={() => setIsConfirmOpen(false)}
          />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="activate-directed-title"
              className="pointer-events-auto max-w-md rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-lg"
            >
              <h4 id="activate-directed-title" className="m-0 text-lg font-bold text-brand-navy font-display">
                Activar notificaciones
              </h4>
              <p className="mt-3 text-sm text-text-muted font-body">
                Se activarán las notificaciones push para este proyecto. ¿Estás segura de que quieres continuar?
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsConfirmOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
                  disabled={isActivating}
                  onClick={() => {
                    handleConfirmActivation().catch(() => {});
                  }}
                >
                  {isActivating ? 'Activando...' : 'Aceptar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
export default AlertsDirectedConfig;
